import React, { useEffect, useState, useContext } from "react";
import {
  Route,
  Routes,
  useNavigate,
  Navigate,
  useLocation,
} from "react-router-dom";
import Dashboard from "./components/dashboard/Dashboard";
import ProvidersSearchResults from "./components/searchlist/ProvidersSearchResults";
import PracticeSearchResults from "./components/searchlist/PracticeSearchResults";
import ProfileInformation from "./components/providers/profile-information/ProfileInformation";
import ProviderPhotos from "./components/providers/provider-info/ProviderPhotos";
import ProviderLocation from "./components/providers/provider-info/ProviderLocation";
import ProviderInfo from "./components/providers/provider-info/ProviderInfo";
import Experience from "./components/providers/experience/Experience";
import TestCanvas from "./Canvas/TestCanvas";
import PracticeProfileInformation from "./components/practice/profile-information/PracticeProfileInformation";
import PracticeInfo from "./components/practice/practice-info/PracticeInfo";
import PracticePhotos from "./components/practice/practice-info/PracticePhotos";
import PracticeHighlights from "./components/practice/practice-highlights/PracticeHighlights";
import FeeSchedules from "./components/practice/feeSchedules/FeeSchedules";
import Services from "./components/practice/services/Services";
import ProviderLicenseSection from "./components/providers/provider-info/ProviderLicenseSection";
import GoogleMapComponent from "./common/GoogleMapComponent";
import Reviews from "./components/practice/reviews/Reviews";
import Manageusers from "./components/practice/manageusers/Manageusers";
import OfficeHoursWrapper from "./components/practice/officeHours/OfficeHoursWrapper";
import ProvidersDetails from "./components/practice/providers/ProvidersDetails";
import AddPractice from "./components/admin/AddPractice";
import AddProviders from "./components/admin/AddProviders";
import Myaccount from "./components/myaccount/Myaccount";
import LoginInformation from "./components/myaccount/LoginInformation";
import ChangePassword from "./components/myaccount/ChangePassword";
import UserManagement from "./components/admin/UserManagement";
import ContactUs from "./components/dashboard/ContactUs";
import Mfa from "./components/login/Mfa";
import PinVerification from "./components/login/PinVerification";
import RefreshToken from "./components/login/RefreshToken";
import ForgotPassword from "./components/login/ForgotPassword";
import LoginFirst from "./components/login/LoginFirst";
import LandingPageBanner from "./components/header/LandingPageBanner";
import MainHeader from "./components/header/MainHeader";
import MyPractice from "./components/practice/MyPractice";
import { MyAccountContext } from "./App";
import ResetPassword from "./components/login/resetPassword";

const AppRouter = () => {
  const [token, setToken] = useState(false);
  const { grantedRoles = [] } = useContext(MyAccountContext) || {};
  const hasPracticeRole = grantedRoles.includes("ROLE_PRACTICE");
  const hasManagerRole = grantedRoles.includes("ROLE_MANAGER");

  const navigateTo = useNavigate();
  const windowLocation = useLocation();

  useEffect(() => {
    const token = localStorage.getItem("auth-key");
    if (token) {
      setToken(true);
    } else {
      setToken(false);
    }
  }, []);


  useEffect(() => {
    if (token && windowLocation.pathname === "/") {
      if (hasPracticeRole) {
        navigateTo("/my-practice", { replace: true });
      } else if (hasManagerRole) {
        navigateTo("/dashboard", { replace: true });
      }
    }
  }, [token, hasPracticeRole, hasManagerRole, windowLocation.pathname, navigateTo]);

  const getUserDisplayId = () => {
    const searchParams = new URLSearchParams(windowLocation.search);
    let id = searchParams.get("userDisplayId");

    if (!id && windowLocation.hash) {
      const hashParts = windowLocation.hash.split("?");
      if (hashParts.length > 1) {
        const hashParams = new URLSearchParams(hashParts[1]);
        id = hashParams.get("userDisplayId");
      }
    }

    return id;
  };

  const userDisplayId = getUserDisplayId();

  const getUserByToken = () => {
    const searchParams = new URLSearchParams(windowLocation.search);
    return searchParams.get("token");
  };

  const userToken = getUserByToken();

  const isChangePasswordUrl = () => {
    return windowLocation.pathname.includes("change-password");
  };

  const isCreateAccountViaEmailLinkUrl = () => {
    return windowLocation.pathname.includes("create-account-via-email-link");
  };

  const isAccountSetupFlow =
    isCreateAccountViaEmailLinkUrl() ||
    (userDisplayId && !isChangePasswordUrl());

  const isPasswordResetFlow =
    isChangePasswordUrl() || (userToken && !isCreateAccountViaEmailLinkUrl());

  useEffect(() => {
    /**
     *  Redirects unauthenticated users accessing via host URL.
     *  Skips redirection for home, change-password, create-account-via-email-link,
     *  or if userDisplayId is present.
     */

    const token = localStorage.getItem("auth-key");
    if (
      token ||
      userDisplayId ||
      isChangePasswordUrl() ||
      isCreateAccountViaEmailLinkUrl()
    ) {
      return;
    }

    if (window.location.hash === "") {
      navigateTo("/home", { replace: true });
      return;
    }
    if (window.location.hash === "#/home") {
      return;
    }
    if (window.location.pathname === "/") {
      navigateTo("/login", { replace: true });
    }
  }, []);

  return (
    <div>
      {(token || windowLocation.pathname === "/forgotPassword") && (
        <MainHeader />
      )}
      <Routes>
        {!token ? (
          <>
            {isAccountSetupFlow ? (
              <>
                <Route path="/" element={<LoginFirst />} />
                <Route
                  path="/create-account-via-email-link"
                  element={<LoginFirst />}
                />
              </>
            ) : isPasswordResetFlow ? (
              <>
                <Route path="/change-password" element={<ResetPassword />} />
              </>
            ) : (
              <>
                <Route path="/home*" element={<LandingPageBanner />} />
                <Route path="/login" element={<LoginFirst />} />
                <Route path="/forgotPassword" element={<ForgotPassword />} />
                <Route path="/create-account*" element={<LoginFirst />} />
              </>
            )}
          </>
        ) : (
          <>
            <Route
              path="/"
              element={
                hasPracticeRole ? (
                  <Navigate to="/my-practice" />
                ) : hasManagerRole ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <Navigate to="/dashboard" />
                )
              }
            />

            <Route path="/dashboard" element={<Dashboard />}>
              <Route path="providers" element={<ProvidersSearchResults />} />
              <Route path="practices" element={<PracticeSearchResults />} />
            </Route>
            {/* <Route path="/home*" element={<Navigate to="/dashboard" replace />} /> */}
            <Route path="/my-practice" element={<MyPractice />} />
            <Route path="canvas" element={<TestCanvas />} />
            <Route
              path={`/provider/:id/profile-information`}
              element={<ProfileInformation />}
            >
              <Route path="info" element={<ProviderInfo />} />
              <Route path="photos" element={<ProviderPhotos />} />
              <Route path="license" element={<ProviderLicenseSection />} />
              <Route path="experience" element={<Experience />} />
              <Route path="location" element={<ProviderLocation />} />
            </Route>

            <Route
              path="/practice/:id/profile-information/*"
              element={<PracticeProfileInformation />}
            >
              <Route path="info" element={<PracticeInfo />} />
              <Route path="photos" element={<PracticePhotos />} />
              <Route path="highlights" element={<PracticeHighlights />} />
              <Route path="officeHours" element={<OfficeHoursWrapper />} />
              <Route path="fee-schedules" element={<FeeSchedules />} />
              <Route path="services" element={<Services />} />

              <Route path="provider" element={<ProvidersDetails />} />
              <Route path="reviews" element={<Reviews />} />
              <Route path="manageusers" element={<Manageusers />} />
            </Route>
            <Route path="/contactus" element={<ContactUs />} />

            <Route path="/googleMap" element={<GoogleMapComponent />}></Route>
            <Route path="addPractice" element={<AddPractice />} />
            <Route path="user-management" element={<UserManagement />} />
            <Route path="addProviders" element={<AddProviders />} />

            <Route path="/account-settings" element={<Myaccount />}>
              <Route path="logininformation" element={<LoginInformation />} />
              <Route path="changepassword" element={<ChangePassword />} />
            </Route>
            <Route path="mfa" element={<Mfa />} />
            <Route path="pinverification" element={<PinVerification />} />
            <Route path="sessiontimeout" element={<RefreshToken />} />

            {/* {--------- Local testing purpose only --------- } */}
            <Route path="localtest" element={<TestCanvas />} />

            <Route path="loginFirst" element={<LoginFirst />} />
          </>
        )}
      </Routes>
    </div>
  );
};

export default AppRouter;
